import React, { useRef, useEffect, useState } from "react";
import io from "socket.io-client";
import WindowHeader from "../../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../../NavBars/ActionToolbar";
import SherComboBox from "../../../ComboBoxes/SherComboBox";
import ssLogo from "../../../../assets/images/sslogo.png";
import axios from "axios";
import { useDbContext } from "../../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../../app/permissionsSlice";

const emitStockGroupUpdate = (apiBaseUrl) => {
  if (!apiBaseUrl) return;
  const socket = io(apiBaseUrl);
  socket.emit("StockGroupUpdated");
  socket.disconnect();
};

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor"
    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor"
    strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const HierarchyIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="18" r="2.5" />
    <circle cx="6" cy="6" r="2.5" />
    <circle cx="18" cy="18" r="2.5" />
    <path d="M6 8.5v5" />
    <path d="M8.5 18h7" />
    <path d="M6 13.5a4.5 4.5 0 0 1 4.5 4.5" />
  </svg>
);

const TreeNode = ({ node, level = 0, onSelect, selectedName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isSelected = selectedName === node.name;

  const handleClick = () => {
    if (node.isGroup) {
      setIsOpen((prev) => !prev);
    }
    if (onSelect) onSelect(node);
  };

  return (
    <div className="flex flex-col select-none">
      <div
        onClick={handleClick}
        className={`
          flex items-center py-[4px] cursor-pointer transition-colors duration-100 rounded-sm
          ${isSelected ? 'bg-[#d6e4f7]' : 'hover:bg-[#EAF0FC]'}
        `}
        style={{ paddingLeft: `${level * 14 + 4}px` }}
      >
        <span className="w-4 flex justify-center items-center shrink-0 text-[#213CA5]">
          {node.isGroup
            ? isOpen ? <ChevronDown /> : <ChevronRight />
            : null
          }
        </span>
        <span
          className={`
            text-[12px] uppercase tracking-wide truncate leading-5
            ${node.isGroup
              ? 'text-[#213CA5] font-bold'
              : 'text-[#5A7DC0] font-medium pl-1'
            }
          `}
        >
          {node.name}
        </span>
      </div>

      {node.isGroup && isOpen && node.children?.length > 0 && (
        <div>
          {node.children.map((child, index) => (
            <TreeNode
              key={`${child.name}-${index}`}
              node={child}
              level={level + 1}
              onSelect={onSelect}
              selectedName={selectedName}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function StockGroupReg({ onClose, zIndex }) {
  const permissions = useSelector(selectMenuPermissions);
  const hasSavePermission = permissions["stockGroup"]?.actions?.Save ?? true;
  const hasEditPermission = permissions["stockGroup"]?.actions?.Edit ?? true;
  const hasDeletePermission = permissions["stockGroup"]?.actions?.Delete ?? true;

  const groupNameInputRef = useRef(null);
  const parentInputRef = useRef(null);
  const purityInputRef = useRef(null);
  const saveBtnRef = useRef(null);

  const [formData, setFormData] = useState({
    groupName: "",
    parentGroup: "",
    purityClass: "",
  });
  const [selectedId, setSelectedId] = useState("");
  const [saveDisabled, setSaveDisabled] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();

  const [parentOptions, setParentOptions] = useState([]);
  const [purityOptions, setPurityOptions] = useState([]);
  const [stockGroupsList, setStockGroupsList] = useState([]);
  const [treeData, setTreeData] = useState([]);

  const fetchData = async () => {
    if (!dbCode) return;
    try {
      // 1. Fetch Rate Classes (Purity) from RateClass table
      const rateClassRes = await axios.get(`${apiBaseUrl}/main/selectall/${dbCode}`);
      let mappedRateClasses = [];
      if (rateClassRes.data && Array.isArray(rateClassRes.data)) {
        mappedRateClasses = rateClassRes.data.map((item) => ({
          label: item.rateClass || "",
          value: String(item.id || item.Auto || ""),
          id: item.id || item.Auto,
          touchPercent: item.touchPercent
        }));
        setPurityOptions(mappedRateClasses);
      }

      // 2. Fetch Stock Parents from StockParent table
      const parentRes = await axios.get(`${apiBaseUrl}/common/getStockParents/${dbCode}`);
      let mappedParents = [];
      if (parentRes.data && Array.isArray(parentRes.data)) {
        mappedParents = parentRes.data.map((item) => ({
          label: item.ParentName || "",
          value: String(item.Auto || item.id || ""),
          id: item.Auto || item.id,
          code: item.Code
        }));
        setParentOptions(mappedParents);
      }

      // 3. Fetch Stock Groups from StockGroup table
      const stockGroupRes = await axios.get(`${apiBaseUrl}/common/getStockGroups/${dbCode}`);
      const groupsData = stockGroupRes.data || [];
      setStockGroupsList(groupsData);

      // 4. Construct Dynamic Tree View
      const dynamicTree = mappedParents.map((parent) => {
        const children = groupsData
          .filter(sg => String(sg.Parent) === String(parent.value) || sg.ParentName === parent.label)
          .map(sg => ({
            name: sg.GroupName,
            isGroup: false,
            auto: sg.Auto,
            parentGroup: String(sg.Parent || ""),
            purityClass: String(sg.RateClass || ""),
          }));

        return {
          name: parent.label,
          isGroup: true,
          children: children,
        };
      });

      // Add orphaned / uncategorized stock groups if any
      const parentIds = new Set(mappedParents.map(p => String(p.value)));
      const parentNames = new Set(mappedParents.map(p => p.label));
      const uncategorized = groupsData.filter(
        sg => !parentIds.has(String(sg.Parent)) && !parentNames.has(sg.ParentName)
      );

      if (uncategorized.length > 0) {
        dynamicTree.push({
          name: "OTHER GROUPS",
          isGroup: true,
          children: uncategorized.map(sg => ({
            name: sg.GroupName,
            isGroup: false,
            auto: sg.Auto,
            parentGroup: String(sg.Parent || ""),
            purityClass: String(sg.RateClass || ""),
          }))
        });
      }

      setTreeData(dynamicTree);

    } catch (error) {
      console.error("Error fetching StockGroup data:", error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, [apiBaseUrl, dbCode]);

  useEffect(() => {
    setTimeout(() => {
      groupNameInputRef.current?.focus?.();
    }, 150);
  }, []);

  const handleClear = () => {
    setFormData({
      groupName: "",
      parentGroup: "",
      purityClass: "",
    });
    setSelectedId("");
    groupNameInputRef.current?.clearInput?.();
    parentInputRef.current?.clearInput?.();
    purityInputRef.current?.clearInput?.();
    setTimeout(() => {
      groupNameInputRef.current?.focus?.();
    }, 100);
  };

  const handleSelectRecord = (record) => {
    const autoVal = record.Auto || record.auto;
    setSelectedId(autoVal ? String(autoVal) : "");
    setFormData({
      groupName: record.GroupName || record.name || "",
      parentGroup: record.Parent != null ? String(record.Parent) : (record.parentGroup || ""),
      purityClass: record.RateClass != null ? String(record.RateClass) : (record.purityClass || ""),
    });
  };

  const getValidSelectedIds = () => {
    if (!formData.groupName || formData.groupName.trim() === "") {
      alert("Please enter a Group Name");
      groupNameInputRef.current?.focus?.();
      return null;
    }

    if (!formData.parentGroup || String(formData.parentGroup).trim() === "") {
      alert("Please select a Parent Group");
      parentInputRef.current?.focus?.();
      return null;
    }

    const matchedParent = parentOptions.find(
      (opt) => String(opt.value) === String(formData.parentGroup) || opt.label.toLowerCase() === String(formData.parentGroup).toLowerCase()
    );

    if (!matchedParent) {
      alert("Please select a valid Parent Group from the dropdown options.");
      parentInputRef.current?.focus?.();
      return null;
    }

    if (!formData.purityClass || String(formData.purityClass).trim() === "") {
      alert("Please select a Purity Class");
      purityInputRef.current?.focus?.();
      return null;
    }

    const matchedPurity = purityOptions.find(
      (opt) => String(opt.value) === String(formData.purityClass) || opt.label.toLowerCase() === String(formData.purityClass).toLowerCase()
    );

    if (!matchedPurity) {
      alert("Please select a valid Purity Class from the dropdown options.");
      purityInputRef.current?.focus?.();
      return null;
    }

    return {
      parentId: parseInt(matchedParent.value),
      rateClassId: parseInt(matchedPurity.value),
    };
  };

  const handleFind = () => {
    if (!formData.groupName || formData.groupName.trim() === "") {
      alert("Please enter or select a Group Name to find.");
      groupNameInputRef.current?.focus?.();
      return;
    }

    const searchTerm = formData.groupName.trim().toLowerCase();
    const matchedRecord = stockGroupsList.find(
      (item) => item.GroupName?.toLowerCase() === searchTerm || String(item.Auto) === selectedId
    );

    if (matchedRecord) {
      handleSelectRecord(matchedRecord);
      // alert(`Stock Group "${matchedRecord.GroupName}" found!`);
    } else {
      // alert(`Stock Group "${formData.groupName}" not found.`);
    }
  };

  const handleSubmit = async () => {
    const validatedIds = getValidSelectedIds();
    if (!validatedIds) return;

    const isDuplicate = stockGroupsList.some(
      (item) => item.GroupName?.toLowerCase() === formData.groupName.toLowerCase().trim()
    );

    if (isDuplicate) {
      alert("Duplicate entry. Stock Group already exists.");
      return;
    }

    if (!window.confirm(`Are you sure you want to save stock group "${formData.groupName.trim()}"?`)) {
      return;
    }

    setSaveDisabled(true);
    try {
      const response = await axios.post(
        `${apiBaseUrl}/common/insertStockGroup/${dbCode}`,
        {
          groupName: formData.groupName.trim(),
          parent: validatedIds.parentId,
          rateClass: validatedIds.rateClassId,
        }
      );

      if (response.status === 200 || response.data?.success) {
        alert("Stock Group Saved Successfully!");
        emitStockGroupUpdate(apiBaseUrl);
        handleClear();
        fetchData();
      } else {
        alert("Error submitting data.");
      }
    } catch (error) {
      console.error("Error saving stock group:", error.message);
      alert("Error submitting data.");
    } finally {
      setSaveDisabled(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedId) {
      alert("Please select or find a Stock Group to edit");
      return;
    }

    const validatedIds = getValidSelectedIds();
    if (!validatedIds) return;

    if (!window.confirm(`Are you sure you want to update stock group "${formData.groupName.trim()}"?`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${apiBaseUrl}/common/updateStockGroup/${dbCode}`,
        {
          auto: parseInt(selectedId),
          groupName: formData.groupName.trim(),
          parent: validatedIds.parentId,
          rateClass: validatedIds.rateClassId,
        }
      );

      if (response.status === 200 || response.data?.success) {
        alert("Stock Group Updated Successfully!");
        emitStockGroupUpdate(apiBaseUrl);
        handleClear();
        fetchData();
      } else {
        alert("Error updating data.");
      }
    } catch (error) {
      console.error("Error updating stock group:", error.message);
      alert("Error updating data.");
    }
  };

  const handleDelete = async () => {
    if (!selectedId) {
      alert("Please select a Stock Group to delete");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${formData.groupName}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${apiBaseUrl}/common/deleteStockGroup/${dbCode}/${selectedId}`
      );
      if (response.status === 200 || response.data?.success) {
        alert("Stock Group Deleted Successfully!");
        emitStockGroupUpdate(apiBaseUrl);
        handleClear();
        fetchData();
      } else {
        alert("Error deleting data.");
      }
    } catch (error) {
      console.error("Error deleting stock group:", error.message);
      alert("Error deleting data.");
    }
  };

  const handleKeyDown = (event, nextInputRef) => {
    if (event.key === "Enter" || event.key === "Return") {
      event.preventDefault();
      nextInputRef?.current?.focus?.();
    }
  };

  // Group ComboBox Options based on stockGroupsList
  const groupComboOptions = stockGroupsList.map((item) => ({
    label: item.GroupName || "",
    value: String(item.Auto || ""),
    ...item
  }));

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 font-Inter select-none"
      style={{ zIndex: zIndex || 101, fontFamily: "Inter, sans-serif" }}
    >
      <div className="bg-white font-normal w-[720px] max-w-[95vw] h-[520px] max-h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        {/* Header */}
        <WindowHeader
          title="Stock Group Registration"
          logo={ssLogo}
          onClose={onClose}
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2"
        />

        {/* Toolbar */}
        <div className="w-full flex-shrink-0">
          <ActionToolbar
            width="100%"
            height="auto"
            bgColor="#EAF0FC"
            size="small"
            iconSize={16}
            showSave={true}
            showPrint={false}
            showClear={true}
            showFind={true}
            showEdit={true}
            showDelete={true}
            showExit={true}
            showNavigation={false}
            saveDisabled={!!selectedId || saveDisabled || !hasSavePermission}
            editDisabled={!selectedId || !hasEditPermission}
            deleteDisabled={!selectedId || !hasDeletePermission}
            findDisabled={false}
            dataFound={!!selectedId}
            onSave={handleSubmit}
            onClear={handleClear}
            onFind={handleFind}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onExit={onClose}
          />
        </div>

        {/* Main Content Body */}
        <div className="w-full flex-1 p-3.5 bg-slate-50 flex gap-4 items-stretch min-h-[300px] overflow-hidden">
          {/* LEFT COLUMN: Add / Edit Form */}
          <div className="flex-1 bg-white p-3.5 rounded-lg border border-slate-200 shadow-sm flex flex-col gap-3 overflow-visible">
            <h3 className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider m-0">
              ADD / EDIT STOCK GROUP
            </h3>

            {/* Group Name */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Group Name
              </label>
              <SherComboBox
                comboRef={groupNameInputRef}
                options={groupComboOptions}
                value={formData.groupName}
                onSelect={(opt) => {
                  handleSelectRecord(opt);
                }}
                onInputChange={(text) => {
                  setFormData((prev) => ({ ...prev, groupName: text }));
                }}
                onFreeInput={(text) => {
                  setFormData((prev) => ({ ...prev, groupName: text }));
                }}
                placeholder="Enter Group Name"
                showArrow={true}
                clearable={false}
                inputWidth="100%"
                inputHeight="32px"
                inputClassName="border border-slate-300 rounded-md focus:border-blue-600 shadow-2xs"
                inputStyle={{ fontSize: "12px", paddingLeft: "8px" }}
                onKeyDown={(e) => handleKeyDown(e, parentInputRef)}
              />
            </div>

            {/* Select Parent */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Select Parent
              </label>
              <SherComboBox
                comboRef={parentInputRef}
                options={parentOptions}
                value={formData.parentGroup}
                onSelect={(opt) => {
                  setFormData((prev) => ({ ...prev, parentGroup: opt.value }));
                }}
                onInputChange={(text) => {
                  setFormData((prev) => ({ ...prev, parentGroup: text }));
                }}
                onFreeInput={(text) => {
                  setFormData((prev) => ({ ...prev, parentGroup: text }));
                }}
                placeholder="Select Parent Group"
                showArrow={true}
                clearable={true}
                inputWidth="100%"
                inputHeight="32px"
                inputClassName="border border-slate-300 rounded-md focus:border-blue-600 shadow-2xs"
                inputStyle={{ fontSize: "12px", paddingLeft: "8px" }}
                onKeyDown={(e) => handleKeyDown(e, purityInputRef)}
              />
            </div>

            {/* Purity Class */}
            <div className="flex flex-col gap-1">
              <label className="text-[11.5px] font-bold text-slate-700 font-Inter">
                Purity Class
              </label>
              <SherComboBox
                comboRef={purityInputRef}
                options={purityOptions}
                value={formData.purityClass}
                onSelect={(opt) => {
                  setFormData((prev) => ({ ...prev, purityClass: opt.value }));
                }}
                onInputChange={(text) => {
                  setFormData((prev) => ({ ...prev, purityClass: text }));
                }}
                onFreeInput={(text) => {
                  setFormData((prev) => ({ ...prev, purityClass: text }));
                }}
                placeholder="Select Purity Class"
                showArrow={true}
                clearable={true}
                inputWidth="100%"
                inputHeight="32px"
                inputClassName="border border-slate-300 rounded-md focus:border-blue-600 shadow-2xs"
                inputStyle={{ fontSize: "12px", paddingLeft: "8px" }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Return") {
                    e.preventDefault();
                    selectedId ? handleEdit() : handleSubmit();
                  }
                }}
              />
            </div>

            {/* Save / Update Button */}
            <button
              type="button"
              ref={saveBtnRef}
              onClick={selectedId ? handleEdit : handleSubmit}
              className="w-full mt-2 py-1.5 px-3 bg-[#1a3abf] hover:bg-[#142e99] active:bg-[#0f2378] text-white font-bold text-xs rounded-lg shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <span>{selectedId ? "Update Group" : "Save Group"}</span>
            </button>
          </div>

          {/* RIGHT COLUMN: Hierarchy Tree */}
          <div className="w-[280px] shrink-0 bg-[#EAF0FC] border border-[#D1E0F5] rounded-lg overflow-hidden flex flex-col">
            <div className="flex-shrink-0 bg-[#213CA5] text-white flex items-center px-3 py-2">
              <HierarchyIcon size={14} />
              <span className="ml-2 text-[12px] font-semibold tracking-wide">Stock Structure</span>
            </div>

            <div className="flex-1 overflow-y-auto p-3 tree-scrollbar bg-white">
              {treeData.map((node, index) => (
                <TreeNode
                  key={`${node.name}-${index}`}
                  node={node}
                  level={0}
                  onSelect={handleSelectRecord}
                  selectedName={formData.groupName}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .tree-scrollbar::-webkit-scrollbar { width: 5px; }
        .tree-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
        .tree-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .tree-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
}

export default StockGroupReg;
